import axios from 'axios';
import { CATALOG } from '../constants/elegantContent';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const normalizeCategory = (category) => {
  if (!category) return 'libro';
  const map = {
    books: 'libro',
    book: 'libro',
    libros: 'libro',
    manga: 'manga',
    mangas: 'manga',
    comics: 'comic',
    comic: 'comic',
    comix: 'comic'
  };
  return map[String(category).toLowerCase()] || String(category).toLowerCase();
};

const fromFallback = () => {
  const resolveCover = (cover) => {
    if (String(cover).startsWith('http://') || String(cover).startsWith('https://')) {
      return cover;
    }
    return `${process.env.PUBLIC_URL || ''}${cover}`;
  };

  const build = (items, category) =>
    items.map((item, index) => ({
      id: item.id,
      title: item.title,
      author: item.author,
      category,
      price: item.price,
      coverUrl: resolveCover(item.cover),
      description: `${item.title} por ${item.author}.`,
      stock: 20 + index,
      publicationDate: `202${index % 6}-0${(index % 9) + 1}-01`,
      rating: item.rating || 4.5,
      language: item.language || 'ES',
      format: item.format || 'Tapa blanda'
    }));

  return [
    ...build(CATALOG.books, 'libro'),
    ...build(CATALOG.manga, 'manga'),
    ...build(CATALOG.comics, 'comic')
  ];
};

const fallbackCatalog = fromFallback();

const parseBook = (book) => {
  const price = Number(book.price);
  const rating = Number(book.rating);

  return {
    id: book.id,
    title: book.title || 'Título desconocido',
    author: book.author || 'Autor desconocido',
    category: normalizeCategory(book.category),
    price: isNaN(price) ? 0 : price,
    coverUrl: book.coverUrl || '',
    description: book.description || '',
    stock: typeof book.stock === 'number' ? book.stock : 0,
    publicationDate: book.publicationDate || null,
    rating: isNaN(rating) ? 4.5 : rating,
    language: book.language || 'ES',
    format: book.format || 'Tapa blanda',
    isbn: book.isbn || ''
  };
};

export const fetchCatalog = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/books`, { params: filters });
    return (response.data || []).map(parseBook);
  } catch (error) {
    let data = [...fallbackCatalog];
    if (filters.category) {
      data = data.filter((book) => normalizeCategory(book.category) === normalizeCategory(filters.category));
    }
    if (filters.q) {
      const q = String(filters.q).toLowerCase();
      data = data.filter(
        (book) => book.title.toLowerCase().includes(q) || book.author.toLowerCase().includes(q)
      );
    }
    if (filters.minPrice !== undefined) {
      data = data.filter((book) => book.price >= Number(filters.minPrice));
    }
    if (filters.maxPrice !== undefined) {
      data = data.filter((book) => book.price <= Number(filters.maxPrice));
    }
    return data;
  }
};

export const fetchBookById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/books/${id}`);
    return parseBook(response.data);
  } catch (error) {
    return fallbackCatalog.find((book) => String(book.id) === String(id)) || null;
  }
};

export const fetchRelatedBooks = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/books/${id}/related`);
    return (response.data || []).map(parseBook);
  } catch (error) {
    const current = fallbackCatalog.find((book) => String(book.id) === String(id));
    if (!current) return [];
    return fallbackCatalog
      .filter((book) => book.category === current.category && String(book.id) !== String(id))
      .slice(0, 6);
  }
};

export const fetchFeaturedBooks = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/books/featured`);
    return (response.data || []).map(parseBook);
  } catch (error) {
    return [...fallbackCatalog].slice(0, 8);
  }
};
